import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import {
  ArrowPathIcon,
  Bars3Icon,
  BugAntIcon,
  ClipboardDocumentListIcon,
  KeyIcon,
  QueueListIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import MultiSelect from '../../components/MultiSelect';
import WorkItemModal from './WorkItemModal';
import NewWorkItemModal from './NewWorkItemModal';
import BulkCreateWorkItemsModal from './BulkCreateWorkItemsModal';
import devopsBoardService, {
  BoardRow,
  BoardTask,
  Iteration,
  SprintBoard,
  TeamMember,
  TokenStatus,
} from '../../services/devopsBoard.service';
import {
  EMPTY_FILTERS,
  Filters,
  allAssignees as collectAssignees,
  filterRows,
  filtersActive as anyFilterActive,
} from './devopsKanbanFilters';
import { isFeature } from './devopsKanbanTags';
import ModalHeader from '../../components/ui/ModalHeader';
import { useModalDrag } from '../../hooks/useModalDrag';

/**
 * DevOps Kanban — the Azure DevOps Taskboard, but the rows can be dragged.
 *
 * DevOps fixes taskboard row order to the sprint backlog stack rank and offers no way
 * to change it from the Taskboard. Dragging a row here rewrites that rank for the whole
 * team, so reordering requires the user's own PAT.
 */

const FILTERS_KEY = 'devopsKanban.filters';
const LANES_KEY = 'devopsKanban.lanes';

/** How often the board quietly re-reads DevOps in the background. */
const AUTO_REFRESH_MS = 10_000;

function loadFilters(): Filters {
  try {
    const raw = localStorage.getItem(FILTERS_KEY);
    return raw ? { ...EMPTY_FILTERS, ...JSON.parse(raw) } : EMPTY_FILTERS;
  } catch {
    return EMPTY_FILTERS;
  }
}

/**
 * Which lanes this person wants on screen. Empty means "all of them", so a board that
 * gains a new state later doesn't silently hide it from everyone who never chose.
 */
function loadLanes(): string[] {
  try {
    const raw = localStorage.getItem(LANES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/** The tag that turns a card orange, matching how we flag work in DevOps. */
const PRIORITY_TAG = 'top-priority';

const STATE_TONE: Record<string, string> = {
  'New': 'bg-gray-400',
  'Business Grooming': 'bg-sky-500',
  'Technical Grooming': 'bg-indigo-500',
  'Ready For Development': 'bg-teal-500',
  'Active': 'bg-amber-500',
  'Blocked': 'bg-red-500',
  'Resolved': 'bg-purple-500',
  'Closed': 'bg-green-600',
  'Removed': 'bg-gray-500',
};

/** Left accent stripe, mirroring the work-item colors DevOps uses. */
function accentFor(type: string): string {
  if (type === 'Bug') return 'border-l-red-500';
  if (type === 'Task') return 'border-l-amber-500';
  return 'border-l-blue-500'; // User Story
}

function TypeIcon({ type, className = 'h-3.5 w-3.5' }: { type: string; className?: string }) {
  if (type === 'Bug') return <BugAntIcon className={`${className} text-red-500`} />;
  if (type === 'Task') return <ClipboardDocumentListIcon className={`${className} text-amber-500`} />;
  return <QueueListIcon className={`${className} text-blue-500`} />;
}

/**
 * State control on a card — close or advance an item without opening the modal.
 *
 * Only Tasks are auto-assigned to whoever moves them off New; changing a story's state
 * here never reassigns it.
 */
const StatePicker: React.FC<{
  workItemId: number;
  state: string;
  states: string[];
  canEdit: boolean;
  tags: string[];
  onChangeState: (id: number, state: string) => void;
}> = ({ workItemId, state, states, canEdit, tags, onChangeState }) => {
  const tone = STATE_TONE[state] ?? 'bg-gray-400';
  if (!canEdit) {
    return (
      <span className="flex items-center gap-1">
        <span className={`inline-block h-1.5 w-1.5 rounded-full ${tone}`} />
        {state}
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1">
      <span className={`inline-block h-1.5 w-1.5 shrink-0 rounded-full ${tone}`} />
      <select
        value={state}
        // Keep the click off the drag handle wrapping this card.
        onMouseDown={e => e.stopPropagation()}
        onClick={e => e.stopPropagation()}
        onChange={e => onChangeState(workItemId, e.target.value)}
        className={`max-w-[9rem] truncate rounded border-0 bg-transparent px-0.5 py-0
                    text-[0.6875rem] focus:ring-1 focus:ring-blue-400 ${
          tags.includes(PRIORITY_TAG) ? 'text-gray-800' : 'text-muted'
        }`}
      >
        {states.map(s => <option key={s} value={s}>{s}</option>)}
        {!states.includes(state) && <option value={state}>{state}</option>}
      </select>
    </span>
  );
};

/**
 * Assignee control on a card.
 *
 * The value written is the member's email — DevOps resolves people by identity, and
 * display names are not unique. Someone assigned who has since left the team is kept
 * as an option so opening the dropdown doesn't silently blank them.
 */
const AssigneePicker: React.FC<{
  workItemId: number;
  assignedTo: string | null;
  members: TeamMember[];
  canEdit: boolean;
  tags: string[];
  onAssign: (id: number, email: string) => void;
}> = ({ workItemId, assignedTo, members, canEdit, tags, onAssign }) => {
  if (!canEdit || members.length === 0) {
    return (
      <span className="flex items-center gap-1">
        <Avatar name={assignedTo} />
        {assignedTo ?? 'Unassigned'}
      </span>
    );
  }

  const current = members.find(m => m.name === assignedTo);
  return (
    <span className="flex items-center gap-1">
      <Avatar name={assignedTo} />
      <select
        value={current?.email ?? ''}
        // Keep the click off the drag handle wrapping this card.
        onMouseDown={e => e.stopPropagation()}
        onClick={e => e.stopPropagation()}
        onChange={e => onAssign(workItemId, e.target.value)}
        className={`max-w-[9rem] truncate rounded border-0 bg-transparent px-0.5 py-0
                    text-[0.6875rem] focus:ring-1 focus:ring-blue-400 ${
          tags.includes(PRIORITY_TAG)
            ? 'text-gray-800'
            : 'text-muted'
        }`}
      >
        <option value="">Unassigned</option>
        {members.map(m => <option key={m.email} value={m.email}>{m.name}</option>)}
        {assignedTo && !current && (
          <option value="" disabled>{assignedTo} (not on team)</option>
        )}
      </select>
    </span>
  );
};

/** Initials bubble standing in for the DevOps avatar. */
function Avatar({ name }: { name: string | null }) {
  const initials = (name ?? '?')
    .split(' ')
    .map(p => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <span
      className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full
                 bg-gray-300 dark:bg-gray-600 text-[0.5rem] font-semibold
                 text-gray-700 dark:text-gray-100"
      title={name ?? 'Unassigned'}
    >
      {initials}
    </span>
  );
}

/** Gold star on anything tagged Feature, so new features stand out from bugs and chores. */
function FeatureStar({ tags }: { tags: string[] }) {
  if (!isFeature(tags)) return null;
  return (
    <StarIcon
      className="h-3.5 w-3.5 shrink-0 text-yellow-500 drop-shadow-sm"
      title="Feature"
      aria-label="Feature"
      data-testid="feature-star"
    />
  );
}

function TagPills({ tags }: { tags: string[] }) {
  if (!tags.length) return null;
  return (
    <div className="flex flex-wrap gap-1 pt-1">
      {tags.map(t => (
        <span
          key={t}
          className={`rounded px-1.5 py-0.5 text-[0.625rem] ${
            t === PRIORITY_TAG
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200'
          }`}
        >
          {t}
        </span>
      ))}
    </div>
  );
}

/**
 * Card surface. Normal work is a plain card that follows the app theme; anything
 * tagged top-priority goes orange in both themes, the way it reads in DevOps.
 */
function cardClasses(type: string, tags: string[]): string {
  const base = `rounded border border-l-4 ${accentFor(type)} px-2 py-1.5 text-xs shadow-sm`;
  return tags.includes(PRIORITY_TAG)
    ? `${base} bg-orange-400 dark:bg-orange-500 border-orange-500 dark:border-orange-600`
    : `${base} bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700`;
}

/** Text has to stay legible on the orange fill in both themes. */
function textClasses(tags: string[]): string {
  return tags.includes(PRIORITY_TAG)
    ? 'text-gray-900'
    : 'text-gray-800 dark:text-gray-100';
}

function subTextClasses(tags: string[]): string {
  return tags.includes(PRIORITY_TAG)
    ? 'text-gray-800'
    : 'text-muted';
}

const DevOpsKanban: React.FC = () => {
  const [iterations, setIterations] = useState<Iteration[]>([]);
  const [iterationPath, setIterationPath] = useState<string>('');
  const [board, setBoard] = useState<SprintBoard | null>(null);
  const [token, setToken] = useState<TokenStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>(loadFilters);
  const [lanes, setLanes] = useState<string[]>(loadLanes);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [openWorkItemId, setOpenWorkItemId] = useState<number | null>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [bulkCreate, setBulkCreate] = useState<{
    iterationPath: string;
    parent?: { id: number; title: string };
  } | null>(null);
  // null = closed; {} = new story/bug; {parent} = new task under that story.
  const [creating, setCreating] =
    useState<{ parent?: { id: number; title: string } } | null>(null);

  // Refs, not state: the polling closure needs the value at fire time, and a drag
  // must not trigger a re-render mid-gesture.
  const draggingRef = useRef(false);
  const savingRef = useRef(false);

  useEffect(() => {
    localStorage.setItem(FILTERS_KEY, JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    localStorage.setItem(LANES_KEY, JSON.stringify(lanes));
  }, [lanes]);

  // Sprint list + token status, once.
  useEffect(() => {
    (async () => {
      try {
        const [its, tok, opts] = await Promise.all([
          devopsBoardService.listIterations(),
          devopsBoardService.getTokenStatus(),
          // Assignee choices; the board still works if this fails.
          devopsBoardService.getOptions('User Story').catch(() => null),
        ]);
        setIterations(its.data);
        setToken(tok);
        setMembers(opts?.members ?? []);
        // Default to the sprint covering today, else the last one.
        const today = new Date().toISOString().slice(0, 7);
        const match = its.data.find(i => i.name === today) ?? its.data[its.data.length - 1];
        setIterationPath(match?.path ?? '');
      } catch {
        setError('Could not reach Azure DevOps. Check the integration is configured.');
        setLoading(false);
      }
    })();
  }, []);

  const loadBoard = useCallback(async (path: string) => {
    if (!path) return;
    setLoading(true);
    setError(null);
    try {
      setBoard(await devopsBoardService.getSprint(path));
    } catch {
      setError('Could not load this sprint from Azure DevOps.');
      setBoard(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadBoard(iterationPath); }, [iterationPath, loadBoard]);

  /**
   * Quiet background refresh: swap in DevOps' current state with no spinner and no
   * flicker, so someone else's changes appear on their own.
   *
   * Deliberately skipped while a drag is in flight, while a write is saving, or while
   * the tab is hidden — landing a poll response mid-drag would yank cards out from
   * under the cursor or resurrect the state we just overwrote.
   */
  useEffect(() => {
    if (!iterationPath) return;
    const timer = setInterval(async () => {
      if (draggingRef.current || savingRef.current || document.hidden) return;
      try {
        const fresh = await devopsBoardService.getSprint(iterationPath);
        if (draggingRef.current || savingRef.current) return; // raced while in flight
        setBoard(fresh);
        setLastRefreshed(new Date());
      } catch {
        // A failed poll is not worth interrupting the page for; the next one retries.
      }
    }, AUTO_REFRESH_MS);
    return () => clearInterval(timer);
  }, [iterationPath]);

  const iteration = iterations.find(i => i.path === iterationPath);
  const rows = board?.rows ?? [];
  const columns = board?.columns ?? [];

  // Story owners AND task assignees — filtering to a person must find their tasks,
  // not just stories they own.
  const allAssignees = useMemo(() => collectAssignees(rows), [rows]);
  // Every state in the process, in workflow order — not just the ones that happen to
  // be present, so the filter list doesn't change shape as work moves.
  const allStates = columns;
  const allTypes = useMemo(
    () => Array.from(new Set(rows.map(r => r.type))).sort(),
    [rows],
  );

  // An empty lane selection means "show everything".
  const shownColumns = useMemo(
    () => (lanes.length ? columns.filter(c => lanes.includes(c)) : columns),
    [columns, lanes],
  );

  const visibleRows = useMemo(() => filterRows(rows, filters), [rows, filters]);

  const canReorder = Boolean(board?.canReorder);
  // Reordering a filtered list is well-defined, but sorting the view by anything other
  // than rank would make "drop here" meaningless, so there is no sort control.
  const filtersActive = anyFilterActive(filters);

  const handleDragEnd = async (result: DropResult) => {
    draggingRef.current = false;
    if (!result.destination || !board || !iteration) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;

    const movedId = visibleRows[from].id;
    const previous = board;

    // Optimistic: reorder the full row list to match the visible move.
    const reorderedVisible = [...visibleRows];
    const [moved] = reorderedVisible.splice(from, 1);
    reorderedVisible.splice(to, 0, moved);
    const visibleSet = new Set(visibleRows.map(r => r.id));
    let cursor = 0;
    const optimistic = rows.map(r =>
      visibleSet.has(r.id) ? reorderedVisible[cursor++] : r,
    );
    setBoard({ ...board, rows: optimistic });
    setSaving(true);
    savingRef.current = true;
    setError(null);

    try {
      await devopsBoardService.reorder({
        iterationId: iteration.id,
        iterationPath,
        movedId,
        targetIndex: to,
        visibleIds: visibleRows.map(r => r.id),
      });
      // Re-read so we show DevOps' truth, not our guess at it.
      await loadBoard(iterationPath);
      setNotice(`Moved ${movedId} to position ${to + 1}.`);
      setTimeout(() => setNotice(null), 3000);
    } catch (e: any) {
      setBoard(previous);
      setError(e?.response?.data?.detail ?? 'The reorder failed and was rolled back.');
    } finally {
      setSaving(false);
      savingRef.current = false;
    }
  };

  /**
   * Dropping a task card in another column changes its State.
   *
   * Each row owns its own DragDropContext, so a task can only move between its own
   * parent's columns — which matches how tasks belong to a story, and avoids nesting
   * droppables inside the row-reorder context.
   */
  const handleTaskDragEnd = async (rowId: number, result: DropResult) => {
    draggingRef.current = false;
    if (!result.destination || !board) return;
    const toState = result.destination.droppableId.split('::')[1];
    const fromState = result.source.droppableId.split('::')[1];
    if (toState === fromState) return;

    const taskId = Number(result.draggableId.split('::')[1]);
    const previous = board;
    const movedTask = rows
      .find(r => r.id === rowId)?.children[fromState]
      ?.find(t => t.id === taskId);

    // Optimistic: move the card across columns before DevOps confirms.
    const optimisticRows = rows.map(r => {
      if (r.id !== rowId) return r;
      const task = (r.children[fromState] ?? []).find(t => t.id === taskId);
      if (!task) return r;
      return {
        ...r,
        children: {
          ...r.children,
          [fromState]: r.children[fromState].filter(t => t.id !== taskId),
          [toState]: [...(r.children[toState] ?? []), { ...task, state: toState }],
        },
      };
    });
    setBoard({ ...board, rows: optimisticRows });
    setSaving(true);
    savingRef.current = true;
    setError(null);

    try {
      const updated = await devopsBoardService.setState(taskId, toState);
      // Taking a task off New claims it, so the assignee may have changed server-side.
      setBoard(b => (b ? {
        ...b,
        rows: b.rows.map(r => r.id !== rowId ? r : {
          ...r,
          children: Object.fromEntries(
            Object.entries(r.children).map(([col, tasks]) => [
              col,
              tasks.map(t => t.id === taskId
                ? { ...t, assignedTo: updated.assignedTo, state: updated.state }
                : t),
            ]),
          ),
        }),
      } : b));
      setNotice(
        updated.assignedTo && updated.assignedTo !== movedTask?.assignedTo
          ? `${taskId} moved to ${toState} and assigned to ${updated.assignedTo}.`
          : `${taskId} moved to ${toState}.`,
      );
      setTimeout(() => setNotice(null), 3000);
    } catch (e: any) {
      setBoard(previous);
      setError(e?.response?.data?.detail ?? 'That state change failed and was rolled back.');
    } finally {
      setSaving(false);
      savingRef.current = false;
    }
  };

  /**
   * Reassign a story or task from its card.
   *
   * An empty value clears the assignee — DevOps treats "" as a real edit, unlike an
   * omitted field.
   */
  /**
   * Change a state from the card dropdown.
   *
   * Reuses the same endpoint as dragging a card between columns, so the claim rule and
   * DevOps' workflow validation behave identically either way. A story moving state
   * refetches, because its card also has to leave or enter a column.
   */
  const changeState = async (workItemId: number, state: string) => {
    const previous = board;
    setSaving(true);
    savingRef.current = true;
    setError(null);
    try {
      await devopsBoardService.setState(workItemId, state);
      await loadBoard(iterationPath);
      setNotice(`${workItemId} set to ${state}.`);
      setTimeout(() => setNotice(null), 3000);
    } catch (e: any) {
      setBoard(previous);
      setError(e?.response?.data?.detail ?? 'That state change could not be saved.');
    } finally {
      setSaving(false);
      savingRef.current = false;
    }
  };

  const assignTo = async (workItemId: number, email: string) => {
    const previous = board;
    setSaving(true);
    savingRef.current = true;
    setError(null);
    try {
      const updated = await devopsBoardService.updateWorkItem(workItemId, {
        assignedTo: email,
      });
      // Patch the one card in place rather than refetching the whole sprint.
      setBoard(b => b && ({
        ...b,
        rows: b.rows.map(r => {
          const row = r.id === workItemId
            ? { ...r, assignedTo: updated.assignedTo } : r;
          return {
            ...row,
            children: Object.fromEntries(
              Object.entries(row.children).map(([col, tasks]) => [
                col,
                tasks.map(t => t.id === workItemId
                  ? { ...t, assignedTo: updated.assignedTo } : t),
              ]),
            ),
          };
        }),
      }));
      setNotice(`${workItemId} assigned to ${updated.assignedTo ?? 'nobody'}.`);
      setTimeout(() => setNotice(null), 3000);
    } catch (e: any) {
      setBoard(previous);
      setError(e?.response?.data?.detail ?? 'That assignment could not be saved.');
    } finally {
      setSaving(false);
      savingRef.current = false;
    }
  };

  return (
    <div className="p-4 space-y-4">
      {/* Title + filters stay pinned; the board scrolls beneath them. */}
      <div className="sticky top-0 z-30 -mx-4 -mt-4 space-y-3 bg-white px-4 pt-4 pb-2 dark:bg-gray-900">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold text-body">
          DevOps Kanban
        </h1>
        <select
          value={iterationPath}
          onChange={e => setIterationPath(e.target.value)}
          className="rounded border border-gray-300 dark:border-gray-600 bg-white
                     dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-gray-100"
        >
          {iterations.map(i => (
            <option key={i.id} value={i.path}>{i.name}</option>
          ))}
        </select>

        <button
          onClick={() => loadBoard(iterationPath)}
          className="inline-flex items-center gap-1 rounded border border-gray-300
                     dark:border-gray-600 px-2 py-1 text-sm text-gray-700 dark:text-gray-200
                     hover:bg-gray-50 dark:hover:bg-gray-700"
          aria-label="Refresh board"
        >
          <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>

        <div className="ml-auto flex items-center gap-2">
          {saving && (
            <span className="text-sm text-muted">Saving…</span>
          )}
          {lastRefreshed && !saving && (
            <span className="text-xs text-gray-400 dark:text-gray-500">
              Updated {lastRefreshed.toLocaleTimeString([], {
                hour: 'numeric', minute: '2-digit',
              })}
            </span>
          )}
          {canReorder && (
            <button
              disabled={!iterationPath}
              onClick={() => setBulkCreate({ iterationPath })}
              className="inline-flex items-center gap-1 rounded border border-blue-600 px-2 py-1
                         text-sm text-blue-600 dark:text-blue-400 disabled:opacity-50"
            >
              <QueueListIcon className="h-4 w-4" /> Bulk create stories
            </button>
          )}
          {canReorder && (
            <button
              onClick={() => setCreating({})}
              className="inline-flex items-center gap-1 rounded bg-blue-600 px-2 py-1
                         text-sm text-white"
            >
              <PlusIcon className="h-4 w-4" /> New Work Item
            </button>
          )}
          <button
            onClick={() => setShowTokenModal(true)}
            className={`inline-flex items-center gap-1 rounded px-2 py-1 text-sm border
              ${canReorder
                ? 'border-green-600 text-green-700 dark:text-green-400'
                : 'border-amber-500 text-amber-700 dark:text-amber-400'}`}
          >
            <KeyIcon className="h-4 w-4" />
            {canReorder ? `Connected${token?.identity ? `: ${token.identity}` : ''}` : 'Connect DevOps'}
          </button>
        </div>
      </div>

      {!canReorder && !loading && (
        <div className="rounded border border-amber-300 bg-amber-50 dark:bg-amber-900/20
                        dark:border-amber-700 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
          Read-only. Connect your own Azure DevOps token to drag rows — priority changes
          are made as you, using your DevOps permissions.
        </div>
      )}

      {error && (
        <div className="rounded border border-red-300 bg-red-50 dark:bg-red-900/20
                        dark:border-red-700 px-3 py-2 text-sm text-red-800 dark:text-red-200">
          {error}
        </div>
      )}
      {notice && (
        <div className="rounded border border-green-300 bg-green-50 dark:bg-green-900/20
                        dark:border-green-700 px-3 py-2 text-sm text-green-800 dark:text-green-200">
          {notice}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <input
          value={filters.keyword}
          onChange={e => setFilters({ ...filters, keyword: e.target.value })}
          placeholder="Filter by keyword or id"
          className="rounded border border-gray-300 dark:border-gray-600 bg-white
                     dark:bg-gray-800 px-2 py-1 text-gray-900 dark:text-gray-100 w-56"
        />
        <div className="w-52">
          <MultiSelect
            options={allStates.map(s => ({ value: s, label: s }))}
            value={filters.states}
            onChange={v => setFilters({ ...filters, states: v })}
            placeholder="All states"
            size="sm"
            preserveOrder
            usePortal
            enableSearch
          />
        </div>
        <div className="w-52">
          <MultiSelect
            options={allAssignees.map(a => ({ value: a, label: a }))}
            value={filters.assignees}
            onChange={v => setFilters({ ...filters, assignees: v })}
            placeholder="All people"
            size="sm"
            usePortal
            enableSearch
          />
        </div>
        <div className="w-44">
          <MultiSelect
            options={allTypes.map(t => ({ value: t, label: t }))}
            value={filters.types}
            onChange={v => setFilters({ ...filters, types: v })}
            placeholder="All types"
            size="sm"
            usePortal
          />
        </div>
        <div className="w-56">
          <MultiSelect
            options={columns.map(c => ({ value: c, label: c }))}
            value={lanes}
            onChange={setLanes}
            placeholder="All lanes"
            size="sm"
            preserveOrder
            usePortal
            showClearOption
            clearOptionLabel="All lanes"
          />
        </div>
        {filtersActive && (
          <button
            onClick={() => setFilters(EMPTY_FILTERS)}
            className="inline-flex items-center gap-1 text-gray-500 dark:text-gray-400
                       hover:text-gray-800 dark:hover:text-gray-200"
          >
            <XMarkIcon className="h-4 w-4" /> Clear
          </button>
        )}
        <span className="ml-auto text-muted">
          {visibleRows.length} of {rows.length} items
        </span>
      </div>

      {filtersActive && canReorder && (
        <p className="text-xs text-muted">
          Filters are on. Dropping a row places it directly beneath the row above it,
          even when hidden items sit in between.
        </p>
      )}
      </div>

      {/* Board.
          The board owns its own scrolling in both directions and is bounded to the
          viewport, so the column headers can stick to the top of it while rows scroll
          underneath — and still slide sideways in step with the lanes. */}
      <div className="overflow-auto max-h-[calc(100vh-16rem)] border-t border-gray-200 dark:border-gray-700">
        <div className="min-w-[900px]">
          {/* Column headers */}
          <div
            className="sticky top-0 z-20 grid gap-2 border-b border-gray-200 dark:border-gray-700
                       bg-white dark:bg-gray-900 pt-2 pb-1 mb-1"
            style={{ gridTemplateColumns: `minmax(320px, 1.4fr) repeat(${shownColumns.length}, minmax(150px, 1fr))` }}
          >
            <div className="text-xs font-semibold uppercase text-muted">
              Backlog Priority
            </div>
            {shownColumns.map(c => (
              <div key={c}
                className="text-xs font-semibold uppercase text-muted">
                {c}
              </div>
            ))}
          </div>

          {loading ? (
            <div className="py-10 text-center text-muted">Loading…</div>
          ) : (
            <DragDropContext
              onDragStart={() => { draggingRef.current = true; }}
              onDragEnd={handleDragEnd}
            >
              <Droppable droppableId="board-rows">
                {provided => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {visibleRows.map((row, index) => (
                      <Draggable
                        key={row.id}
                        draggableId={String(row.id)}
                        index={index}
                        isDragDisabled={!canReorder}
                      >
                        {(dragProvided, snapshot) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            className={`grid gap-2 py-1 border-b border-gray-100
                                        dark:border-gray-800 ${
                              snapshot.isDragging ? 'bg-blue-50 dark:bg-blue-900/30 shadow-lg' : ''
                            }`}
                            style={{
                              gridTemplateColumns:
                                `minmax(320px, 1.4fr) repeat(${shownColumns.length}, minmax(150px, 1fr))`,
                              ...dragProvided.draggableProps.style,
                            }}
                          >
                            <ParentRow
                              row={row}
                              position={index + 1}
                              canReorder={canReorder}
                              dragHandleProps={dragProvided.dragHandleProps}
                              onOpen={setOpenWorkItemId}
                              members={members}
                              onAssign={assignTo}
                              states={columns}
                              onChangeState={changeState}
                            />
                            {/* Own context per row: tasks move between this story's
                                columns only, and never nest inside the row context. */}
                            <DragDropContext
                              onDragStart={() => { draggingRef.current = true; }}
                              onDragEnd={r => handleTaskDragEnd(row.id, r)}
                            >
                              {shownColumns.map(col => (
                                <Droppable
                                  key={col}
                                  droppableId={`${row.id}::${col}`}
                                  isDropDisabled={!canReorder}
                                >
                                  {(cellProvided, cellSnapshot) => (
                                    <div
                                      ref={cellProvided.innerRef}
                                      {...cellProvided.droppableProps}
                                      className={`space-y-1 min-h-[2.5rem] rounded p-0.5
                                        ${cellSnapshot.isDraggingOver
                                          ? 'bg-blue-100 dark:bg-blue-900/40'
                                          : ''}`}
                                    >
                                      {(row.children[col] ?? []).map((task, ti) => (
                                        <Draggable
                                          key={task.id}
                                          draggableId={`${row.id}::${task.id}`}
                                          index={ti}
                                          isDragDisabled={!canReorder}
                                        >
                                          {(tp, ts) => (
                                            <div
                                              ref={tp.innerRef}
                                              {...tp.draggableProps}
                                              {...tp.dragHandleProps}
                                            >
                                              <TaskCard
                                                task={task}
                                                dragging={ts.isDragging}
                                                onOpen={setOpenWorkItemId}
                                                members={members}
                                                canEdit={canReorder}
                                                onAssign={assignTo}
                                                states={columns}
                                                onChangeState={changeState}
                                              />
                                            </div>
                                          )}
                                        </Draggable>
                                      ))}
                                      {cellProvided.placeholder}
                                      {/* Mirrors the taskboard's green + : add a task
                                          to this story, in its first column only. */}
                                      {canReorder && col === shownColumns[0] && (
                                        <div className="space-y-1">
                                          <button
                                            onClick={() => setCreating({
                                              parent: { id: row.id, title: row.title },
                                            })}
                                            className="flex w-full items-center justify-center
                                                       rounded border border-dashed
                                                       border-gray-300 py-0.5 text-gray-400
                                                       hover:border-blue-400 hover:text-blue-500
                                                       dark:border-gray-600"
                                            aria-label={`Add a task to ${row.id}`}
                                          >
                                            <PlusIcon className="h-3.5 w-3.5" />
                                          </button>
                                          <button
                                            onClick={() => setBulkCreate({
                                              iterationPath,
                                              parent: { id: row.id, title: row.title },
                                            })}
                                            className="flex w-full items-center justify-center gap-1 rounded
                                                       border border-dashed border-gray-300 py-1 text-xs
                                                       text-gray-500 hover:border-blue-400 hover:text-blue-500
                                                       dark:border-gray-600 dark:text-gray-400"
                                            aria-label={`Bulk add tasks to ${row.id}`}
                                          >
                                            <QueueListIcon className="h-3.5 w-3.5" /> Bulk add tasks
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </Droppable>
                              ))}
                            </DragDropContext>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    {visibleRows.length === 0 && (
                      <div className="py-10 text-center text-muted">
                        No items match these filters.
                      </div>
                    )}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </div>
      </div>

      {bulkCreate && (
        <BulkCreateWorkItemsModal
          iterationPath={bulkCreate.iterationPath}
          parent={bulkCreate.parent}
          onClose={() => setBulkCreate(null)}
          onCreated={() => loadBoard(iterationPath)}
        />
      )}

      {creating && (
        <NewWorkItemModal
          iterationPath={iterationPath}
          parent={creating.parent}
          members={members}
          onClose={() => setCreating(null)}
          onCreated={() => loadBoard(iterationPath)}
        />
      )}

      {openWorkItemId !== null && (
        <WorkItemModal
          workItemId={openWorkItemId}
          canEdit={canReorder}
          onClose={() => setOpenWorkItemId(null)}
          onSaved={() => loadBoard(iterationPath)}
        />
      )}

      {showTokenModal && (
        <TokenModal
          status={token}
          onClose={() => setShowTokenModal(false)}
          onChanged={async () => {
            setToken(await devopsBoardService.getTokenStatus());
            await loadBoard(iterationPath);
          }}
        />
      )}
    </div>
  );
};

const ParentRow: React.FC<{
  row: BoardRow;
  position: number;
  canReorder: boolean;
  dragHandleProps: any;
  onOpen: (id: number) => void;
  members: TeamMember[];
  onAssign: (id: number, email: string) => void;
  states: string[];
  onChangeState: (id: number, state: string) => void;
}> = ({ row, position, canReorder, dragHandleProps, onOpen, members, onAssign,
        states, onChangeState }) => (
  <div className="flex items-start gap-1 pr-2">
    <div
      {...dragHandleProps}
      className={canReorder ? 'cursor-grab active:cursor-grabbing pt-2' : 'pt-2 opacity-30'}
      aria-label={canReorder ? `Drag to reprioritize ${row.id}` : undefined}
    >
      <Bars3Icon className="h-4 w-4 text-gray-400" />
    </div>
    <span className="w-6 shrink-0 pt-2 text-right text-[0.625rem] text-gray-400">{position}</span>
    <div className={`${cardClasses(row.type, row.tags)} min-w-0 flex-1`}>
      <div className="flex items-start gap-1">
        <span className="pt-0.5"><TypeIcon type={row.type} /></span>
        <span className="pt-0.5"><FeatureStar tags={row.tags} /></span>
        <button
          onClick={() => onOpen(row.id)}
          className={`font-semibold hover:underline ${
            row.tags.includes(PRIORITY_TAG)
              ? 'text-gray-900'
              : 'text-blue-600 dark:text-blue-400'
          }`}
        >
          {row.id}
        </button>
        <span className={`${textClasses(row.tags)} ${row.isClosed ? 'line-through opacity-60' : ''}`}>
          {row.title}
        </span>
      </div>
      <div className={`flex items-center gap-1 pt-1 ${subTextClasses(row.tags)}`}>
        <StatePicker
          workItemId={row.id}
          state={row.state}
          states={states}
          canEdit={canReorder}
          tags={row.tags}
          onChangeState={onChangeState}
        />
      </div>
      <div className={`flex items-center gap-1 pt-1 ${subTextClasses(row.tags)}`}>
        <AssigneePicker
          workItemId={row.id}
          assignedTo={row.assignedTo}
          members={members}
          canEdit={canReorder}
          tags={row.tags}
          onAssign={onAssign}
        />
      </div>
      <TagPills tags={row.tags} />
    </div>
  </div>
);

const TaskCard: React.FC<{
  task: BoardTask;
  dragging?: boolean;
  onOpen: (id: number) => void;
  members: TeamMember[];
  canEdit: boolean;
  onAssign: (id: number, email: string) => void;
  states: string[];
  onChangeState: (id: number, state: string) => void;
}> = ({ task, dragging, onOpen, members, canEdit, onAssign, states, onChangeState }) => (
  <div
    className={`${cardClasses(task.type, task.tags)} ${
      dragging ? 'shadow-lg ring-2 ring-blue-400' : ''
    } cursor-grab active:cursor-grabbing`}
  >
    <div className="flex items-start gap-1">
      <span className="pt-0.5"><TypeIcon type={task.type} /></span>
      <span className="pt-0.5"><FeatureStar tags={task.tags} /></span>
      <button
        onClick={e => { e.stopPropagation(); onOpen(task.id); }}
        className={`font-semibold hover:underline ${
          task.tags.includes(PRIORITY_TAG)
            ? 'text-gray-900'
            : 'text-blue-600 dark:text-blue-400'
        }`}
      >
        {task.id}
      </button>
      <span className={`${textClasses(task.tags)} line-clamp-2`}>{task.title}</span>
    </div>
    <div className={`flex items-center gap-1 pt-1 ${subTextClasses(task.tags)}`}>
      <StatePicker
        workItemId={task.id}
        state={task.state}
        states={states}
        canEdit={canEdit}
        tags={task.tags}
        onChangeState={onChangeState}
      />
    </div>
    <div className={`flex items-center gap-1 pt-1 ${subTextClasses(task.tags)}`}>
      <AssigneePicker
        workItemId={task.id}
        assignedTo={task.assignedTo}
        members={members}
        canEdit={canEdit}
        tags={task.tags}
        onAssign={onAssign}
      />
    </div>
    <TagPills tags={task.tags} />
  </div>
);

const TokenModal: React.FC<{
  status: TokenStatus | null;
  onClose: () => void;
  onChanged: () => Promise<void>;
}> = ({ status, onClose, onChanged }) => {
  const [pat, setPat] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(true);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await devopsBoardService.saveToken(pat.trim());
      await onChanged();
      onClose();
    } catch (e: any) {
      setErr(e?.response?.data?.detail ?? 'Could not save that token.');
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await devopsBoardService.deleteToken();
      await onChanged();
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/40">
      <div
        ref={panelRef}
        style={panelStyle}
        className="w-full max-w-lg rounded-lg bg-panel"
      >
        <ModalHeader
          title="Connect Azure DevOps"
          onClose={busy ? undefined : onClose}
          dragHandleProps={dragHandleProps}
        />
        <div className="p-4 space-y-3">
        {status?.available === false ? (
          <p className="text-sm text-amber-700 dark:text-amber-300">{status.reason}</p>
        ) : (
          <>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Reordering is done as you, so it needs your own token. Create one in DevOps
              under <em>User settings → Personal access tokens</em> with the
              {' '}<strong>Work Items → Read &amp; write</strong> scope. Nothing else is needed.
            </p>
            {status?.connected && (
              <p className="text-sm text-green-700 dark:text-green-400">
                Currently connected as {status.identity}.
              </p>
            )}
            <input
              type="password"
              value={pat}
              onChange={e => setPat(e.target.value)}
              placeholder="Paste your personal access token"
              className="w-full rounded border border-gray-300 dark:border-gray-600
                         bg-white dark:bg-gray-900 px-2 py-1 text-sm
                         text-gray-900 dark:text-gray-100"
            />
            {err && <p className="text-sm text-red-600 dark:text-red-400">{err}</p>}
            <div className="flex justify-end gap-2">
              {status?.connected && (
                <button
                  onClick={disconnect}
                  disabled={busy}
                  className="rounded border border-gray-300 dark:border-gray-600 px-3 py-1
                             text-sm text-gray-700 dark:text-gray-200"
                >
                  Disconnect
                </button>
              )}
              <button
                onClick={save}
                disabled={busy || pat.trim().length < 10}
                className="rounded bg-blue-600 px-3 py-1 text-sm text-white disabled:opacity-50"
              >
                {busy ? 'Checking…' : 'Save'}
              </button>
            </div>
          </>
        )}
        </div>
      </div>
    </div>
  );
};

export default DevOpsKanban;
